import React, { useEffect, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import ContainerDimensions from 'react-container-dimensions';
import FullStationDetails from './FullStationDetails';
import StationDot from './StationDot';
import StationName from './StationName';
import StateChangedPopup from './StateChangedPopup';
import IncidentUnderstaffingPopup from './IncidentUnderstaffingPopup';
import { selectors as arSchedulesSelectors } from '../../state/modules/availability-requirement-schedules';
import { stationToIncidentsMap } from '../../state/modules/incidents/selectors';
import { selectors as dashboardSelectors } from '../../state/modules/dashboards';
import { selectors as groupSelectors } from '../../state/modules/groups';
import SettingsForm from '../dashboards/widgets/ui-kit/SettingsForm';
import DashboardAPIManager from '../dashboards/DashboardAPIManager';
import { normalSimplifiedMap } from './MapStyles';
import { isUndefined } from 'lodash';
import GoogleMapReact, { fitBounds } from 'google-map-react';

const MapSettings = ({ updateWidgetSettingsField, accessScopeGroups, groupsTree, widgetSettings }) => {
  const { visible_availability_requirements } = widgetSettings;
  const visibleAvailabilityRequirements =
    visible_availability_requirements ||
    accessScopeGroups.flatMap(g => g.availability_requirements?.map(ar => ar.id) || []);

  const displayModeOptions = [
    {
      value: 'full',
      name: I18n.t('dashboards.widget_archetypes.region_map.settings.full')
    },
    {
      value: 'name_only',
      name: I18n.t('dashboards.widget_archetypes.region_map.settings.name_only')
    },
    {
      value: 'dot',
      name: I18n.t('dashboards.widget_archetypes.region_map.settings.dots')
    }
  ];

  return (
    <SettingsForm updateWidgetSettingsField={updateWidgetSettingsField} widgetSettings={widgetSettings}>
      <SettingsForm.Section>
        <SettingsForm.Section.Header>{I18n.t('display')}</SettingsForm.Section.Header>
        <SettingsForm.Section.Content>
          <SettingsForm.Select
            fieldName="displayMode"
            labelColWidth={7}
            options={displayModeOptions}
            label={I18n.t('dashboards.widget_archetypes.region_map.settings.display_mode')}
          />
          <SettingsForm.ToggleField
            fieldName="showPopup"
            label={I18n.t('dashboards.widget_archetypes.region_map.settings.show_popup')}
          />
          <SettingsForm.ToggleField
            fieldName="showIncidents"
            label={I18n.t('dashboards.widget_archetypes.region_map.settings.show_incidents')}
          />
          <SettingsForm.ToggleField
            fieldName="audibleIncidentUnderstaffingWarning"
            label={I18n.t('dashboards.widget_archetypes.region_map.settings.audible_incident_understaffing_warning')}
          />
          <SettingsForm.AvailabilityRequirementsSelector
            groupsTree={groupsTree}
            parentGroupId={accessScopeGroups}
            selectedAvailabilityRequirements={visibleAvailabilityRequirements}
            onChange={v => updateWidgetSettingsField('visible_availability_requirements', v)}
          />
        </SettingsForm.Section.Content>
      </SettingsForm.Section>
    </SettingsForm>
  );
};

const loadingElement = <div style={{ height: '100%' }} />;
const mapElement = <div style={{ height: '100%' }} />;

const Map = props => {
  const { groupId, accessScopeGroups, stationsGroupedByCoordinates, visible_availability_requirements } = props;

  useEffect(() => {
    // Since the region map is also used outside of dashboards, it is needed to instantiate the
    // API Manager because there is no dashboard to pass it as a prop
    const dashboardAPIManager = new DashboardAPIManager();
    dashboardAPIManager.setDefaultGroupId(groupId);
    dashboardAPIManager.fetchAvailabilityCodes();
    dashboardAPIManager.fetchSkills();
    dashboardAPIManager.fetchGroupAndSubgroups(groupId);
    dashboardAPIManager.registerForAvailabilityRequirementScheduleUpdates({ groupId });
    dashboardAPIManager.registerForIncidentUpdates({ groupId });
  }, [groupId]);

  const createContainerElement = (containerHeight, windowHeight) => {
    // this is a bit nasty: when it's on the dashboard, the RegionMap gets the
    // size from the container. When it's in stand alone mode, the
    // containerHeight is equal to the padding of an empty widget: 10 px
    // If we have an decent container height, we use it. Otherwise, we use the
    // windowHeight - the size of the navigation bar (60px)
    let height = containerHeight < 40 ? windowHeight - 60 : containerHeight - 10;
    if (props.forceUseWindowHeight || props.singleWidgetMode) height = windowHeight - 60;

    return <div style={{ height }} />;
  };

  const isAvailabilityRequirementVisible = useCallback(
    availabilityRequirement => {
      if (!visible_availability_requirements) return true;

      return visible_availability_requirements.includes(availabilityRequirement.id);
    },
    [visible_availability_requirements]
  );

  // Only display stations that are descendants of the group the dashboard
  // belongs to
  const filteredStations = useMemo(() => {
    if (accessScopeGroups.length === 0) return stationsGroupedByCoordinates;

    const descendantIds = new Set();
    for (const g of accessScopeGroups) {
      descendantIds.add(g.id);
      for (const id of g.descendant_ids || []) descendantIds.add(id);
    }

    if (descendantIds.size === 0) return stationsGroupedByCoordinates;

    return stationsGroupedByCoordinates
      .map(stationGroup => {
        let stations = stationGroup.stations.map(g => ({
          ...g,
          availability_requirements: g.availability_requirements.filter(ar => isAvailabilityRequirementVisible(ar))
        }));

        stations = stations.filter(g => g.availability_requirements.length > 0 && descendantIds.has(g.id));

        return stations.length > 0 ? { ...stationGroup, stations } : null;
      })
      .filter(Boolean);
  }, [stationsGroupedByCoordinates, accessScopeGroups, isAvailabilityRequirementVisible]);

  const displayPopup = isUndefined(props.showPopup) ? true : props.showPopup;

  return (
    <ContainerDimensions>
      {({ width, height }) => (
        <>
          <RegionMap
            incidents={props.incidentsByGroupId}
            arCurrentState={props.arCurrentState}
            displayMode={props.displayMode}
            stationsGroupedByCoordinates={filteredStations}
            loadingElement={loadingElement}
            mapElement={mapElement}
            containerElement={createContainerElement(height, window.innerHeight)}
            width={width}
            skills={props.skills}
            showIncidents={props.showIncidents}
          />
          {displayPopup && (
            <StateChangedPopup
              groups={props.stations}
              visibleAvailabilityRequirementIds={visible_availability_requirements}
            />
          )}
          <IncidentUnderstaffingPopup
            groups={props.stations}
            incidents={props.incidentsByGroupId}
            audibleWarningEnabled={props.audibleIncidentUnderstaffingWarning}
          />
        </>
      )}
    </ContainerDimensions>
  );
};

const chooseDisplayLevelComponent = displayMode => {
  const components = { dot: StationDot, name_only: StationName, full: FullStationDetails };
  return components[displayMode] || FullStationDetails;
};

const options = {
  styles: normalSimplifiedMap,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false
};

const StationMarker = props => {
  const StationTag = chooseDisplayLevelComponent(props.displayMode);
  return (
    <>
      {props.stationGroup.stations.map(station => (
        <StationTag
          key={station.id}
          availabilityRequirementState={props.arCurrentState}
          group={station}
          incidents={props.incidents[station.id] || {}}
          skills={props.skills}
          showIncidents={props.showIncidents}
        />
      ))}
    </>
  );
};

const RegionMap = ({
  stationsGroupedByCoordinates,
  width,
  containerElement,
  skills,
  displayMode,
  incidents,
  arCurrentState,
  showIncidents
}) => {
  const { center, zoom } = useMemo(() => {
    if (stationsGroupedByCoordinates.length === 0) return { center: null, zoom: null };

    const ne = { lat: null, lng: null };
    const sw = { lat: null, lng: null };

    for (const stationGroup of stationsGroupedByCoordinates) {
      const { lat, lng } = stationGroup.coordinates;
      ne.lat = Math.max(ne.lat || lat, lat);
      ne.lng = Math.max(ne.lng || lng, lng);
      sw.lat = Math.min(sw.lat || lat, lat);
      sw.lng = Math.min(sw.lng || lng, lng);
    }

    const limits = { ne, sw };
    const size = { height: containerElement.props.style.height, width: width };

    return fitBounds(limits, size);
  }, [stationsGroupedByCoordinates, width, containerElement.props.style.height]);

  if (center === null) return null;

  return (
    <div style={{ height: containerElement.props.style.height, width: '100%' }}>
      <GoogleMapReact bootstrapURLKeys={{ key: globalThis.mapsApiKey }} center={center} zoom={zoom} options={options}>
        {stationsGroupedByCoordinates &&
          stationsGroupedByCoordinates.map(stationGroup => (
            <StationMarker
              skills={skills}
              key={stationGroup.key}
              stationGroup={stationGroup}
              displayMode={displayMode}
              incidents={incidents}
              arCurrentState={arCurrentState}
              showIncidents={showIncidents}
              {...stationGroup.coordinates}
            />
          ))}
      </GoogleMapReact>
    </div>
  );
};

const mapStateToProps = state => ({
  arCurrentState: arSchedulesSelectors.selectDashboardState(state),
  incidentsByGroupId: stationToIncidentsMap(state),
  stationsGroupedByCoordinates: groupSelectors.stationsGroupedByCoordinates(state),
  stations: groupSelectors.stations(state),
  groupsTree: groupSelectors.tree(state),
  skills: state.skills,
  accessScopeGroups: dashboardSelectors.selectCurrentDashboardAccessScopeGroups(state)
});

const ConnectedMap = connect(mapStateToProps)(Map);

const mapStateToPropsSettings = state => ({
  groupsTree: groupSelectors.tree(state),
  accessScopeGroups: dashboardSelectors.selectCurrentDashboardAccessScopeGroups(state)
});

const ConnectedMapSettings = connect(mapStateToPropsSettings)(MapSettings);

Object.assign(ConnectedMap, {
  SettingsContent: ConnectedMapSettings,
  defaultSettings: {
    displayMode: 'full',
    showPopup: true,
    showIncidents: false,
    audibleIncidentUnderstaffingWarning: true
  }
});

export default ConnectedMap;
